import { Box, BoxProps, Button, Grid, Hidden, styled, Typography, useMediaQuery } from '@mui/material';
import Divider from '../components_sunnyside/divider';
import SunnysideGrid from '../components_sunnyside/grid';
import SunnysideHeader from '../components_sunnyside/header';
import Image from '../components_sunnyside/image';
import SunnysideWrapper from '../components_sunnyside/wrapper';
import { colors } from '../styles/_colors';
import HeroImage from '../../public/img/alternative-home/10/hero.png';
import TrustpilotImage from '../../public/img/alternative-home/10/trustpilot.png';
import PlanningTrackingImage from '../../public/img/alternative-home/10/planning-tracking.png';
import DataInsightsImage from '../../public/img/alternative-home/10/data-insights.png';
import CoachingImage from '../../public/img/alternative-home/10/coaching.png';
import CommunityImage from '../../public/img/alternative-home/10/community.png';
import GainMoreEnergyImage from '../../public/img/alternative-home/10/gain-more-energy.png';
import ImproveYourWellnessImage from '../../public/img/alternative-home/10/improve-your-wellness.png';
import SleepBetterImage from '../../public/img/alternative-home/10/sleep-better.png';
import CutEmptyCaloriesImage from '../../public/img/alternative-home/10/cut-empty-calories.png';
import SaveMoneyImage from '../../public/img/alternative-home/10/save-money.png';
import DrinkLessAlcohol from '../../public/img/alternative-home/10/drink-less-alcohol.png';
import TakeTheQuizImage from '../../public/img/alternative-home/10/take-the-quiz.png';
import GetAPersonalizedPlanImage from '../../public/img/alternative-home/10/get-a-personalized-plan.png';
import StickWithItImage from '../../public/img/alternative-home/10/stick-with-it.png';
import WhatIsMindfulDrinkingImage from '../../public/img/alternative-home/10/what-is-mindful-drinking.png';
import AccentIcon from '../../public/img/alternative-home/10/accent-icon.png';
import HomeScience2 from '../sections/home/home_science_2';
import ProductHuntImage from '../../public/img/alternative-home/10/product-hunt.png';
import SunnysideMain from '../components_sunnyside/main';
import BrightestSection from '../sections/reusable/brightest';
import SunnysideFooter from '../components_sunnyside/footer';
import theme from '../theme';
import { diagnosticLink as getDiagnosticLink } from '../utils/diagnosticLinks';
import { useRouter } from 'next/router';
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { Data, get } from '../networking';
import Cookies from 'cookies';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { ampIdentifySet, ampLogEvent } from '../lib/amplitude';

const CORE_TEXT_STYLES = {
    '&.white': {
        color: 'white'
    },
    '&.center': {
        textAlign: 'center'
    }
};

async function getCouponInfo(id?: string, context?: GetServerSidePropsContext): Promise<Data> {
    if (!id) {
        return;
    }
    return await get(`payments-gateway/stripe/coupon/${id}`, {}, context);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cookies = new Cookies(context.req, context.res);
    const couponId = cookies.get('ss_discount') || context.query.d;
    const couponResponse = await getCouponInfo(couponId, context);

    return {
        props: {
            couponResponse: couponResponse || {},
            query: context.query
        },
    };
};

export default function HomePage10({
    couponResponse,
    query
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element {
    const router = useRouter();
    const [ cookies, setCookie ] = useCookies();
    const isXS = useMediaQuery(theme.breakpoints.down('sm'));
    const diagnosticLink = (): string => {
        const url = new URL(getDiagnosticLink());
        const query = router.query;
        Object.keys(query).forEach(k => {
            const value = query[k];
            url.searchParams.append(k, Array.isArray(value) ? JSON.stringify(value) : value);
        });
        return url.href;
    };
    const { utm_source } = query;
    const productHunt = utm_source?.toLowerCase() === 'product-hunt';

    const Hero = styled(Box)(({ theme }) => ({
        '& .header-box': {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '100%'
        },
        '& .img-outer-box': {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            height: '100%'
        },
        '& .btn-box': {
            position: 'relative',
            paddingTop: '1.5rem',
            width: 'fit-content',
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                paddingTop: '1rem'
            }
        },
        '& .img-box': {
            display: 'flex',
            justifyContent: 'flex-end'
        },
        '& p': {
            fontSize: '1.125rem',
            [theme.breakpoints.down('sm')]: {
                fontSize: '.875rem'
            }
        },
        [theme.breakpoints.down('sm')]: {
            textAlign: 'center'
        }
    }));

    const Text = styled('p')(() => ({
        ...CORE_TEXT_STYLES,
        fontSize: '1rem',
        margin: '.5rem 0',
        fontFamily: 'Good Sans Light',
        lineHeight: '1.625rem'
    }));

    const H1 = styled('h1')(({ theme }) => ({
        ...CORE_TEXT_STYLES,
        fontFamily: 'Recoleta Medium',
        fontSize: '3.75rem',
        lineHeight: '3.5rem',
        margin: 0,
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.125rem',
            lineHeight: '2.194rem'
        }
    }));

    const H2 = styled('h2')(({ theme }) => ({
        ...CORE_TEXT_STYLES,
        fontFamily: 'Recoleta Medium',
        fontSize: '2.25rem',
        lineHeight: '2.5rem',
        margin: 0,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.625rem',
            lineHeight: '2rem'
        }
    }));

    const H3 = styled('h3')(({ theme }) => ({
        ...CORE_TEXT_STYLES,
        margin: 0,
        fontSize: '1.75rem',
        fontFamily: 'Recoleta Medium',
        lineHeight: '2.2rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem',
            lineHeight: '1.5rem'
        }
    }));

    const StyledButton = styled(Button)(({ theme }) => ({
        boxShadow: 'unset',
        borderRadius: 20,
        '&:hover': {
            boxShadow: 'unset'
        },
        '&.white': {
            background: 'white',
            color: 'black !important'
        },
        '&.cta': {
            padding: '15px 30px',
            borderRadius: 30,
            fontWeight: 'bold',
            [theme.breakpoints.down('sm')]: {
                fontSize: '1rem'
            }
        },
        color: 'white !important',
        padding: '10px 30px'
    }));

    const SocialProof = styled(Box)(({ theme }) => ({
        paddingTop: '1.5rem',
        paddingBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        height: '100%',
        margin: '0 5.5rem',
        '& p.description': {

        },
        '& p': {
            margin: 0
        },
        '& p.metric': {
            paddingTop: '.8rem',
            fontSize: '1.125rem',
            lineHeight: '.8rem'
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 1rem'
        }
    }));

    const VerticalDivider = styled(Box)(() => ({
        margin: '0 2rem',
        backgroundColor: '#E3DDC7',
        width: 1,
        height: '100%'
    }));

    const Banner = styled(Box)(({ theme }) => ({
        textTransform: 'uppercase',
        color: '#DA552F',
        backgroundColor: 'white',
        width: 'fit-content',
        borderRadius: 8,
        display: 'flex',
        overflow: 'hidden',
        alignItems: 'center',
        padding: '.25rem',
        fontFamily: 'Good Sans Light',
        fontWeight: 'bold',
        marginBottom: '1rem',
        '& p': {
            fontSize: '.8rem',
            margin: 0,
            marginLeft: '.25rem'
        },
        [theme.breakpoints.down('sm')]: {
            marginBottom: 0
        }
    }));

    const Section = styled(Box)(({ theme }) => ({
        '& .img-box': {
            display: 'flex',
            justifyContent: 'flex-end',
            '&.border': {
                [theme.breakpoints.down('sm')]: {
                    border: 'solid 1px #E3E3E3',
                    paddingTop: '2rem'
                }
            }
        },
        '& .btn-outer-box': {
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            height: '100%',
            '&.center': {
                alignItems: 'center'
            }
        },
        '& .content-box': {
            marginBottom: '1rem',
            '& p': {
                paddingRight: '1.5rem'
            }
        },
        '* .content-container': {
            paddingBottom: '2rem'
        }
    }));

    const AccentHeader = styled(Box)(() => ({
        position: 'relative',
        '& .accent': {
            position: 'absolute',
            top: -18,
            left: 0
        },
        '& p': {
            fontSize: '.813rem',
            textTransform: 'uppercase'
        }
    }));

    const ExplanatorySection = styled(Box)(({ theme }) => ({
        '& .item': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            margin: '0 1rem',
            textAlign: 'center',
            '& .content-box': {
                padding: '0 2rem'
            },
            height: '100%'
        },
        '& .header': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            '& p, h2': {
                width: '60%',
                textAlign: 'center',
                [theme.breakpoints.down('sm')]: {
                    width: '100%'
                }
            },
            paddingBottom: '2rem'
        },
        '& .btn-box': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '4rem',
            [theme.breakpoints.down('sm')]: {
                marginTop: '2rem'
            }
        }
    }));

    const TestimonialCard = styled(Box)(() => ({
        backgroundColor: 'white',
        borderRadius: 10,
        padding: '1rem 0',
        border: '1px solid #E4DDC1',
        '& .img-box': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '1rem',
            '& .img': {
                height: 75,
                width: 75,
                backgroundColor: colors.pine,
                borderRadius: '50%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                overflow: 'hidden',
                backgroundSize: 75
            }
        },
        height: '100%',
        width: '100%'
    }));

    const DiscountBanner = styled(Box)<BoxProps>(() => ({
        backgroundColor: '#77BEF8',
        fontSize: '1em',
        textAlign: 'center',
        width: '100%'
    }));

    useEffect(() => {
        ampLogEvent(
            'active: added to experiment', 
            {
                'experiment_name':'exp_202303_purplehomepage', 
                'experiment_variant':'exp_202303_purplehomepage_test'
            },
            cookies
        );
        ampIdentifySet(
            'exp_202303_purplehomepage',
            'exp_202303_purplehomepage_test'
        );
        const domain = (new URL(process.env.NEXT_PUBLIC_SUNNYSIDE_API_HOST));
        let processedDomain;
        if (domain.hostname === 'localhost') {
            processedDomain = 'localhost';
        } else {
            processedDomain = '.' + domain.hostname.match(/(\S+\.)?([\S]+\.com?)$/)[2];
        }
        if (query && query.d) {
            const expiresAt = new Date(new Date().getTime() + (60*60*1000));
            setCookie('ss_discount', query.d, {
                path: '',
                expires: expiresAt,
                domain: processedDomain
            });
        }
        ampLogEvent(
            'signup: landed on homepage',
            {},
            cookies
        );
        ampLogEvent(
            'signup: landed on homepage purple',
            {},
            cookies
        );
    }, []);

    return (
        <SunnysideWrapper>
            {couponResponse?.percent_off && (<DiscountBanner>
                <Typography style={{color: 'white'}}>
                    Congrats! A {couponResponse.percent_off}% discount has been applied for you.
                </Typography>
            </DiscountBanner>)}
            <SunnysideHeader
                bgcolor='#B05DF1'
                whiteLogo
                linkColor='white'
                ctaBtn={{
                    bgColor: '#C27CFA',
                    passQueryToUrl: true
                }}
                iconColor='white'
            />
            <SunnysideGrid maxwidthwrapper background='#B05DF1' paddingBottom={0} paddingTop={0}>
                <Hero>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={6} order={{ xs: 2, sm: 1 }}>
                            <Box className='header-box'>
                                {productHunt && (
                                    <Hidden smDown>
                                        <Banner>
                                            <Image src={ProductHuntImage} width={32} height={31} />
                                            <Text>Welcome Product Hunt</Text>
                                        </Banner>
                                    </Hidden>
                                )}
                                <H1 className='white'>
                                    Cut back on drinking with no pressure to quit.
                                </H1>
                                <Text className='white'>
                                    Build a healthier relationship with alcohol with our science-backed approach to habit change. Simple and on your terms, with no judgment.
                                </Text>
                                <Box className='btn-box'>
                                    <StyledButton href={diagnosticLink()} className='white cta' variant='contained' fullWidth={isXS}>
                                        Start With The 3-Min Quiz
                                    </StyledButton>
                                    <Text className='white center'>Start for free, cancel any time.</Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={5} order={{ xs: 1, sm: 2 }} alignItems='flex-end'>
                            <Box>
                                {productHunt && (
                                    <Hidden smUp>
                                        <Box width='100%' display='flex' justifyContent='center' alignItems='center'>
                                            <Banner>
                                                <Image src={ProductHuntImage} width={32} height={31} />
                                                <Text>Welcome Product Hunt</Text>
                                            </Banner>
                                        </Box>
                                    </Hidden>
                                )}
                                <Box className='btn-outer-box'>
                                    <Box className='img-box'>
                                        <Image src={HeroImage} />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Hero>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper background={colors.beige} noPadding>
                <SocialProof>
                    <Box className='item'>
                        <Image src={TrustpilotImage} height={50} width={113} />
                    </Box>
                    <VerticalDivider />
                    <Box className='item'>
                        <Text className='metric'>
                            <b>150,000+</b>
                        </Text>
                        <Text className='description'>
                            helped so far
                        </Text>
                    </Box>
                    <Hidden smDown>
                        <VerticalDivider />
                        <Box className='item'>
                            <Text className='metric'>
                                <b>Improve overall health</b>
                            </Text>
                            <Text className='description'>
                                most popular top goal
                            </Text>
                        </Box>
                        <VerticalDivider />
                        <Box className='item'>
                            <Text className='metric'>
                                <b>Drink 32% less, save $50+, cut 1,500+ calories</b>
                            </Text>
                            <Text className='description'>
                                average results in 30 days
                            </Text>
                        </Box>
                    </Hidden>
                </SocialProof>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <Section>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={5} order={{ xs: 3, sm: 1 }}>
                            <Box className='content-container'>
                                <Box className='content-box'>
                                    <AccentHeader>
                                        <Box className='accent'>
                                            <Image src={AccentIcon} width={41} height={13} />
                                        </Box>
                                        <Text>Planning & Tracking</Text>
                                    </AccentHeader>
                                    <H2>
                                        
                                        Set targets each week, then track daily to stay accountable.
                                    </H2>
                                    <Text>
                                        We&apos;ve designed a proven system that&apos;s personalized and powerful. Planning ahead sets you up for success, and daily tracking and encouragement make it easier to follow through.
                                    </Text>
                                </Box>
                                <StyledButton href={diagnosticLink()} variant='contained'>
                                    Take the 3 Min Quiz
                                </StyledButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}></Grid>
                        <Grid item xs={12} sm={5} order={{ xs: 1, sm: 3 }}>
                            <Box className='btn-outer-box'>
                                <Box className='img-box border'>
                                    <Image src={PlanningTrackingImage} />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Section>
                <Hidden smDown>
                    <Divider />
                </Hidden>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <Section>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={5}>
                            <Box className='btn-outer-box'>
                                <Box className='img-box border'>
                                    <Image src={DataInsightsImage} />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}></Grid>
                        <Grid item xs={12} sm={5}>
                            <Box className='content-container'>
                                <Box className='content-box'>
                                    <AccentHeader>
                                        <Box className='accent'>
                                            <Image src={AccentIcon} width={41} height={13} />
                                        </Box>
                                        <Text>Data and Insights</Text>
                                    </AccentHeader>
                                    <H2>
                                        Measure your progress in the ways that matter to you.
                                    </H2>
                                    <Text>
                                        See the impact more mindful drinking has on your sleep, your wallet, your diet and your mood. Small changes make a big difference.
                                    </Text>
                                </Box>
                                <StyledButton href={diagnosticLink()} variant='contained'>
                                    Take the 3 Min Quiz
                                </StyledButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Section>
                <Hidden smDown>
                    <Divider />
                </Hidden>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <Section>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={5} order={{ xs: 3, sm: 1 }}>
                            <Box className='content-container'>
                                <Box className='content-box'>
                                    <AccentHeader>
                                        <Box className='accent'>
                                            <Image src={AccentIcon} width={41} height={13} />
                                        </Box>
                                        <Text>Coaching</Text>
                                    </AccentHeader>
                                    <H2>
                                        Real human coaches, always just a text away.
                                    </H2>
                                    <Text>
                                    Work with a human coach as much or as little as you&apos;d like. When you need a little extra nudge in the right direction, our coaches are there for you. It&apos;s just like texting a friend.
                                    </Text>
                                </Box>
                                <StyledButton href={diagnosticLink()} variant='contained'>
                                    Take the 3 Min Quiz
                                </StyledButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}></Grid>
                        <Grid item xs={12} sm={5} order={{ xs: 1, sm: 3 }}>
                            <Box className='btn-outer-box center'>
                                <Box className='img-box border'>
                                    <Image src={CoachingImage} />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Section>
                <Hidden smDown>
                    <Divider />
                </Hidden>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <Section>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={5}>
                            <Box className='btn-outer-box center'>
                                <Box className='img-box border'>
                                    <Image src={CommunityImage} />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}></Grid>
                        <Grid item xs={12} sm={5}>
                            <Box className='content-container'>
                                <Box className='content-box'>
                                    <AccentHeader>
                                        <Box className='accent'>
                                            <Image src={AccentIcon} width={41} height={13} />
                                        </Box>
                                        <Text>Community</Text>
                                    </AccentHeader>
                                    <H2>
                                        Connect with others around common goals.
                                    </H2>
                                    <Text>
                                        The Sunnyside Community is your daily reminder that you&apos;re not alone on this journey. Get inspiration, advice and support from members working towards similar goals.
                                    </Text>
                                </Box>
                                <StyledButton href={diagnosticLink()} variant='contained'>
                                    Take the 3 Min Quiz
                                </StyledButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Section>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper background={colors.beige}>
                <ExplanatorySection>
                    <Box className='header'>
                        <H2>Small changes, big impact</H2>
                        <Text>In just 30 days most members see significant results, and it only gets better over time. Sunnyside sticks with you long after you reach your initial goals, helping you maintain your gains for life.</Text>
                    </Box>
                    <Grid container columnSpacing={2} rowSpacing={5}>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={GainMoreEnergyImage} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Feel more energy
                                    </H3>
                                    <Text>
                                        Greet the day with increased energy and focus.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={ImproveYourWellnessImage} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Improve your wellness
                                    </H3>
                                    <Text>
                                        Always feel your best, inside and out.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={SleepBetterImage} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Sleep better
                                    </H3>
                                    <Text>
                                        Enjoy more restful sleep and brighter mornings.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={CutEmptyCaloriesImage} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Cut empty calories
                                    </H3>
                                    <Text>
                                        Cutting down on drinks has a big impact on your diet and weight.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={SaveMoneyImage} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Save money
                                    </H3>
                                    <Text>
                                        Cut the drinks you don&apos;t need and save cash.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box className='item'>
                                <Box className='img-box'>
                                    <Image src={DrinkLessAlcohol} width={160} height={132} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        Enjoy drinking more
                                    </H3>
                                    <Text>
                                        More intentional drinking is a lot more fun!
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box className='btn-box'>
                        <StyledButton href={diagnosticLink()} variant='contained'>
                            Take the 3 Min Quiz
                        </StyledButton>
                    </Box>
                </ExplanatorySection>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <ExplanatorySection>
                    <Box className='header'>
                        <H2>
                            How it works
                        </H2>
                        <Text>
                            Our science-backed system leverages proven behavioral psychology techniques to help you build new habits that last.
                        </Text>
                    </Box>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <Box className='img-box' style={{ paddingBottom: '1rem' }}>
                                    <Image src={TakeTheQuizImage} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        1. Take the quiz.
                                    </H3>
                                    <Text>
                                        Tell us a little bit about yourself and your goals. This is a judgement-free zone.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <Box className='img-box' style={{ paddingBottom: '1rem' }}>
                                    <Image src={GetAPersonalizedPlanImage} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        2. Get a personalized plan.
                                    </H3>
                                    <Text>
                                        Based on your starting point, we&apos;ll make recommendations and text you reminders to help you meet your goals.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <Box className='img-box' style={{ paddingBottom: '1rem' }}>
                                    <Image src={StickWithItImage} />
                                </Box>
                                <Box className='content-box'>
                                    <H3>
                                        3. Stick with it long-term.
                                    </H3>
                                    <Text>
                                        Think lifestyle change vs. fad diet. We&apos;ll work together to develop tools and habits for long-term wellness.
                                    </Text>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box className='btn-box'>
                        <StyledButton href={diagnosticLink()} variant='contained'>
                            Take the 3 Min Quiz
                        </StyledButton>
                    </Box>
                </ExplanatorySection>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper background={colors.beige}>
                <ExplanatorySection>
                    <Box className='header'>
                        <Box className='accent'>
                            <Image src={AccentIcon} width={99} height={33} />
                        </Box>
                        <H2>
                            The Sunnyside promise:<br></br>
                            Always positive, optimistic and supportive.
                        </H2>
                        <Text>
                            As mindful drinkers ourselves, we celebrate balance. To us that means finding the healthiest way to incorporate alcohol in your life in a way that&apos;s in balance with your other goals. We&apos;ll celebrate your personal goals and ambitions, and pick you up when things don&apos;t go as planned. 
                        </Text>
                    </Box>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <TestimonialCard>
                                    <Box className='img-box'>
                                        <Box
                                            className='img'
                                            style={{
                                                backgroundImage: `url(${'/img/alternative-home/10/testimonial-1.png'})`
                                            }}
                                        />
                                    </Box>
                                    <Box className='content-box'>
                                        <Text>
                                            &quot;No shaming or hassling, very positive and motivating.&quot;
                                        </Text>
                                        <Text>
                                            Romster
                                        </Text>
                                    </Box>
                                </TestimonialCard>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <TestimonialCard>
                                    <Box className='img-box'>
                                        <Box
                                            className='img'
                                            style={{
                                                backgroundImage: `url(${'/img/alternative-home/10/testimonial-2.png'})`
                                            }}
                                        />
                                    </Box>
                                    <Box className='content-box'>
                                        <Text>
                                            &quot;The accountability is a big part of what works for me.&quot;
                                        </Text>
                                        <Text>
                                            CeeCee
                                        </Text>
                                    </Box>
                                </TestimonialCard>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box className='item'>
                                <TestimonialCard>
                                    <Box className='img-box'>
                                        <Box
                                            className='img'
                                            style={{
                                                backgroundImage: `url(${'/img/alternative-home/10/testimonial-3.png'})`
                                            }}
                                        />
                                    </Box>
                                    <Box className='content-box'>
                                        <Text>
                                            &quot;I enjoy alcohol so much more now that I&apos;m being mindful.&quot;
                                        </Text>
                                        <Text>
                                            Jon
                                        </Text>
                                    </Box>
                                </TestimonialCard>
                            </Box>
                        </Grid>
                        <Hidden smDown>
                            <Grid item xs={12} sm={4}>
                                <Box className='item'>
                                    <TestimonialCard>
                                        <Box className='img-box'>
                                            <Box
                                                className='img'
                                                style={{
                                                    backgroundImage: `url(${'/img/alternative-home/10/testimonial-4.png'})`
                                                }}
                                            />
                                        </Box>
                                        <Box className='content-box'>
                                            <Text>
                                                &quot;I&apos;ve been so surprised how I&apos;ve cut my intake in half AND do not feel deprived.&quot;
                                            </Text>
                                            <Text>
                                                Ted
                                            </Text>
                                        </Box>
                                    </TestimonialCard>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box className='item'>
                                    <TestimonialCard>
                                        <Box className='img-box'>
                                            <Box
                                                className='img'
                                                style={{
                                                    backgroundImage: `url(${'/img/alternative-home/10/testimonial-5.png'})`
                                                }}
                                            />
                                        </Box>
                                        <Box className='content-box'>
                                            <Text>
                                                &quot;I&apos;ve since lost weight and saved a ton of money.&quot;
                                            </Text>
                                            <Text>
                                                Abby
                                            </Text>
                                        </Box>
                                    </TestimonialCard>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box className='item'>
                                    <TestimonialCard>
                                        <Box className='img-box'>
                                            <Box
                                                className='img'
                                                style={{
                                                    backgroundImage: `url(${'/img/alternative-home/10/testimonial-6.png'})`
                                                }}
                                            />
                                        </Box>
                                        <Box className='content-box'>
                                            <Text>
                                                &quot;I&apos;ve cut my drinking back to 1/3 of what it was.&quot;
                                            </Text>
                                            <Text>
                                                Bonnie
                                            </Text>
                                        </Box>
                                    </TestimonialCard>
                                </Box>
                            </Grid>
                        </Hidden>
                    </Grid>
                    <Box className='btn-box'>
                        <StyledButton href={diagnosticLink()} variant='contained'>
                            Take the 3 Min Quiz
                        </StyledButton>
                    </Box>
                </ExplanatorySection>
            </SunnysideGrid>
            <SunnysideGrid maxwidthwrapper>
                <Section>
                    <Grid container columnSpacing={2} rowSpacing={3}>
                        <Grid item xs={12} sm={6} order={{ xs: 3, sm: 1 }}>
                            <Box className='content-box'>
                                <H2>
                                    What is mindful drinking?
                                </H2>
                                <Text>
                                    Put simply, it&apos;s all about thinking while drinking. Mindful drinking is not about quitting alcohol but instead being aware of how much alcohol you&apos;re drinking and its impact on you in the moment.
                                </Text>
                                <Text>
                                    It&apos;s about recognizing the reasons for and the ramifications of drinking. It&apos;s about improving your relationship with alcohol so you maximize enjoyment and minimize negative side effects like hangovers, regrets, and anxiety.
                                </Text>
                            </Box>
                            <StyledButton href='https://www.sunnyside.co/blog/mindful-drinking-101' variant='contained'>
                                Read More On The Blog
                            </StyledButton>
                        </Grid>
                        <Grid item xs={12} sm={1}></Grid>
                        <Grid item xs={12} sm={5} order={{ xs: 1, sm: 3 }}>
                            <Image src={WhatIsMindfulDrinkingImage} />
                        </Grid>
                    </Grid>
                </Section>
            </SunnysideGrid>
            <HomeScience2 />
            <SunnysideGrid maxwidthwrapper>
                <ExplanatorySection>
                    <Box className='header'>
                        <H2>
                            Join the growing movement
                        </H2>
                        <Text>
                            As a Sunnyside member you&apos;re part of a fast growing movement focused on getting more out of life by optimizing drinking habits and how they fit into our lives.
                        </Text>
                    </Box>
                    <blockquote className="tiktok-embed"
                        cite="https://www.tiktok.com"
                        data-embed-type="curated"
                        data-video-id-list="7140442778160352518,7158981869642452229,7161915207143394565,7160436105026850053"
                        data-embed-from="embed_page"
                        style={{ maxWidth: '100%', minWidth: 325 }}>
                        <section>
                            <a
                                target="_blank"
                                href="https://www.tiktok.com?refer=embed_page"
                                rel='noreferrer'
                            >
                                TikTok
                            </a>
                        </section>
                    </blockquote>
                    <script async src="https://www.tiktok.com/embed.js"></script>
                </ExplanatorySection>
            </SunnysideGrid>
            <SunnysideMain>
                <BrightestSection />
            </SunnysideMain>
            <SunnysideFooter />
        </SunnysideWrapper>
    );
}
